'use client';

import { useCallback, useEffect, useRef } from 'react';

type MeasurementResult = {
  measureText: (text: string) => number;
  updateFont: (element: HTMLElement) => void;
  setupResizeObserver: (textareaRef: HTMLTextAreaElement, onResize: () => void) => () => void;
};

/**
 * Hook for text measurement and related functionality
 * Returns functions to measure text width, update font settings, and handle resize observations
 */
export default function useTextMeasurement(): MeasurementResult {
  // Canvas reference for text measurement
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Initialize canvas on first render
  useEffect(() => {
    if (canvasRef.current === null) {
      canvasRef.current = document.createElement('canvas');
      ctxRef.current = canvasRef.current.getContext('2d');
    }

    return () => {
      // Clean up
      canvasRef.current = null;
      ctxRef.current = null;
    };
  }, []);

  // Text measurement function
  const measureText = useCallback((text: string): number => {
    if (ctxRef.current === null) {
      return text.length * 8; // Fallback approximation
    }
    return ctxRef.current.measureText(text).width;
  }, []);

  // Update font style based on an element
  const updateFont = useCallback((element: HTMLElement) => {
    if (ctxRef.current === null)
      return;

    const elementStyle = window.getComputedStyle(element);
    const fontStyle = elementStyle.font
      || `${elementStyle.fontSize || '16px'} ${elementStyle.fontFamily || 'sans-serif'}`;

    ctxRef.current.font = fontStyle;
  }, []);

  // Setup resize observer for the textarea
  const setupResizeObserver = useCallback((
    textareaRef: HTMLTextAreaElement,
    onResize: () => void,
  ) => {
    if (resizeObserverRef.current !== null) {
      resizeObserverRef.current.disconnect();
    }

    // Initial font setup
    if (ctxRef.current !== null) {
      updateFont(textareaRef);
    }

    // Create and setup resize observer
    resizeObserverRef.current = new ResizeObserver(() => {
      if (textareaRef !== null && ctxRef.current !== null) {
        updateFont(textareaRef);
        onResize();
      }
    });

    resizeObserverRef.current.observe(textareaRef);
    window.addEventListener('resize', onResize);

    // Return cleanup function
    return () => {
      if (resizeObserverRef.current !== null) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      window.removeEventListener('resize', onResize);
    };
  }, [updateFont]);

  return {
    measureText,
    updateFont,
    setupResizeObserver,
  };
}
